import { createContext } from 'preact'
import { getExpirationTime, clearAllSessionData } from '@/services/session'
import { useEffect, useState, useContext } from 'preact/hooks'
import { useLocation } from 'wouter-preact'

interface ConfigContextType {
  expirationTime: string | null
  setExpirationTime: (time: string | null) => void
  isSessionExpired: boolean
  handleSessionExpired: () => void
  isDocumentSigned: boolean
  setIsDocumentSigned: (signed: boolean) => void
}

const ConfigContext = createContext<ConfigContextType | null>(null)

export const useConfig = () => {
  const context = useContext(ConfigContext)
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider')
  }
  return context
}

export function ConfigProvider({
  children,
}: {
  children: preact.ComponentChildren
}) {
  const [, setLocation] = useLocation()
  const [expirationTime, setExpirationTime] = useState<string | null>(null)
  const [isSessionExpired, setIsSessionExpired] = useState(false)
  const [isDocumentSigned, setIsDocumentSigned] = useState(false)

  useEffect(() => {
    // Initialize with current expiration time
    const time = getExpirationTime()
    setExpirationTime(time)
  }, [])

  const handleSessionExpired = () => {
    setIsSessionExpired(true)
    setLocation('/session-expired')
  }

  const contextValue: ConfigContextType = {
    expirationTime,
    setExpirationTime,
    isSessionExpired,
    handleSessionExpired,
    isDocumentSigned,
    setIsDocumentSigned,
  }

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  )
}
